if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.filterSofiaColor = this.filterSofiaColor.bind(this);

        if (this.elements.thumbnails) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }
      }

      connectedCallback() {
        this.filterSofiaColor();

        const variantEvent =
          typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS.variantChange : 'variant-change';
        if (typeof subscribe === 'function') {
          this.unsubscribeVariantChange = subscribe(variantEvent, () => {
            requestAnimationFrame(this.filterSofiaColor);
            setTimeout(this.filterSofiaColor, 50);
            setTimeout(this.filterSofiaColor, 250);
          });
        }

        this.productInfo = this.closest('product-info');
        this.productInfo?.addEventListener('change', (event) => {
          if (event.target?.matches?.('input[type="radio"]')) {
            this.filterSofiaColor();
            requestAnimationFrame(this.filterSofiaColor);
            setTimeout(this.filterSofiaColor, 250);
          }
        });
      }

      disconnectedCallback() {
        this.unsubscribeVariantChange?.();
      }

      getSelectedColor() {
        const optionName = this.dataset.sofiaColorOption;
        const optionPosition = this.dataset.sofiaColorPosition;
        if (!optionName) return '';

        const root = this.productInfo || this.closest('product-info') || document;
        const expectedName = `${optionName}-${optionPosition}`;
        const checked = [...root.querySelectorAll('input[type="radio"]')].find((input) => {
          const name = (input.name || '').replace(/\s+/g, '');
          return input.checked && (name === expectedName || name.startsWith(`${optionName}-`));
        });
        if (checked?.value) return checked.value.trim();

        const selected = root.querySelector('variant-selects [data-selected-variant]');
        if (!selected?.textContent) return '';
        try {
          const variant = JSON.parse(selected.textContent);
          const index = Number(optionPosition || '1') - 1;
          return (variant.options?.[index] || variant[`option${index + 1}`] || '').toString().trim();
        } catch (error) {
          return '';
        }
      }

      matchesSofiaColor(element, needle) {
        const raw = (element.getAttribute('data-sofia-colors') || '').toLowerCase();
        const tokens = raw.split('|').filter(Boolean);
        if (!needle) return true;
        // Marina-style: only show media mapped to the selected color.
        // Unmapped media stay hidden while a color is selected.
        if (!tokens.length) return false;
        return tokens.includes(needle);
      }

      filterSofiaColor() {
        if (!this.dataset.sofiaColorOption) return;

        const needle = this.getSelectedColor().trim().toLowerCase();
        const scoped = [this, this.closest('product-info')?.querySelector('product-modal')].filter(Boolean);

        scoped.forEach((root) => {
          root.querySelectorAll('[data-sofia-colors]').forEach((element) => {
            const match = this.matchesSofiaColor(element, needle);
            element.classList.toggle('sofia-color-hidden', Boolean(needle) && !match);
          });
        });

        const visibleSlides = Array.from(
          this.elements.viewer?.querySelectorAll('.product__media-item:not(.sofia-color-hidden)') || []
        );
        const active = this.elements.viewer?.querySelector('.product__media-item.is-active');
        if (visibleSlides.length && (!active || active.classList.contains('sofia-color-hidden'))) {
          this.setActiveMedia(visibleSlides[0].dataset.mediaId, true);
        } else if (visibleSlides.length) {
          // Keep carousel scrolled to the active/first visible image after color filter
          const target = active?.classList.contains('sofia-color-hidden') ? visibleSlides[0] : active || visibleSlides[0];
          target?.parentElement?.scrollTo({ left: target.offsetLeft });
          if (this.elements.viewer?.resetPages) this.elements.viewer.resetPages();
        }

        const totalEl = this.querySelector('.slider-counter--total');
        if (totalEl) totalEl.textContent = String(Math.max(visibleSlides.length, 1));

        this.querySelector('.slider-buttons')?.classList.toggle('small-hide', visibleSlides.length <= 1);
        if (this.elements.thumbnails) {
          const visibleThumbs = this.elements.thumbnails.querySelectorAll(
            '.thumbnail-list__item:not(.sofia-color-hidden)'
          );
          this.elements.thumbnails.classList.toggle('hidden', visibleThumbs.length <= 1);
          if (visibleThumbs.length > 1) this.elements.thumbnails.classList.remove('hidden');
        }
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]:not(.sofia-color-hidden)') ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          // Always scroll the peek carousel (mobile + desktop)
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll the page if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail?.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
